// Block shapes (ENG-13/18): the boxes a non-cube block occupies, shared by the
// mesher (geometry) and physics (collision). Pure and isomorphic. A Box is
// [x0,y0,z0, x1,y1,z1] in local [0,1] block space. Cube/fluid geometry is meshed
// by the optimized AO path (not from boxes); cross/torch are meshed specially and
// have no collision. Slab/stairs/layer share one box set for mesh AND collision.

export type Box = readonly [number, number, number, number, number, number];

export const SHAPE = {
  CUBE: 0,
  SLAB: 1,
  STAIRS: 2,
  CROSS: 3,
  TORCH: 4,
  LAYER: 5,
  FLUID: 6,
  DOOR: 7,
  /** Nether portal: a thin, passable, non-mineable plane (DIM-2). */
  PORTAL: 8,
  /** Bed: a low block, 9/16 tall (PLR-10). */
  BED: 9,
  // ── Connecting + panel shapes (ENG-13, M10.2) ──
  /** Fence: centre post + an arm toward each connected side (n/e/s/w props). */
  FENCE: 10,
  /** Fence gate: a bar across when closed, passable when open. */
  FENCE_GATE: 11,
  /** Wall: a thicker post + arms + optional up column. */
  WALL: 12,
  /** Glass/iron pane: a thin cross of arms. */
  PANE: 13,
  /** Trapdoor: a horizontal panel (half top/bottom) that swings vertical when open. */
  TRAPDOOR: 14,
  /** Pointed dripstone: a stack of boxes that narrows toward the point, its width
   *  set by `dripstone_thickness` and its taper direction by `vertical_direction`.
   *  It shipped as a `cross`, which meshed it torch-shaped and gave it no collision
   *  at all — so a stalagmite you were meant to be impaled on was walk-through. */
  DRIPSTONE: 15,
} as const;
export type ShapeId = (typeof SHAPE)[keyof typeof SHAPE];

const FULL: Box = [0, 0, 0, 1, 1, 1];
/** Door panel thickness (3/16), matching Minecraft's door hitbox. */
const DOOR_T = 0.1875;

export function shapeIdFromName(name: string): number {
  switch (name) {
    case 'slab': return SHAPE.SLAB;
    case 'stairs': return SHAPE.STAIRS;
    case 'cross': return SHAPE.CROSS;
    case 'torch': return SHAPE.TORCH;
    case 'layer': return SHAPE.LAYER;
    case 'fluid': return SHAPE.FLUID;
    case 'door': return SHAPE.DOOR;
    case 'portal': return SHAPE.PORTAL;
    case 'bed': return SHAPE.BED;
    case 'fence': return SHAPE.FENCE;
    case 'fence_gate': return SHAPE.FENCE_GATE;
    case 'wall': return SHAPE.WALL;
    case 'pane': return SHAPE.PANE;
    case 'trapdoor': return SHAPE.TRAPDOOR;
    case 'dripstone': return SHAPE.DRIPSTONE;
    default: return SHAPE.CUBE; // cube + any not-yet-modeled shape
  }
}

/** True if the mesher should build this shape from `blockBoxes` (vs the AO cube
 *  path or a special emitter). */
export function isBoxMeshShape(shape: number): boolean {
  return shape === SHAPE.SLAB || shape === SHAPE.STAIRS || shape === SHAPE.LAYER || shape === SHAPE.DOOR
    || shape === SHAPE.BED || shape === SHAPE.FENCE || shape === SHAPE.FENCE_GATE || shape === SHAPE.WALL
    || shape === SHAPE.PANE || shape === SHAPE.TRAPDOOR || shape === SHAPE.DRIPSTONE;
}

/** Half-width of a dripstone segment by its `dripstone_thickness`, in block units.
 *  A spike is widest where it meets the rock and narrowest at the point. */
const DRIPSTONE_HALF: Readonly<Record<string, number>> = {
  base: 0.25, middle: 0.1875, frustum: 0.15625, tip: 0.125,
};

/**
 * The tapered stack a pointed-dripstone segment occupies. A `tip` narrows over
 * three courses toward its point and stops short of the far face (a spike ends in
 * a point, not a flat cap); every other thickness is a straight column of its own
 * width, since the taper across a multi-block spike comes from the thicknesses
 * themselves. `vertical_direction` decides which end is the point: `down` is a
 * stalactite hanging from a ceiling, `up` a stalagmite standing on the floor.
 */
function dripstoneBoxes(props: Readonly<Record<string, string>>): Box[] {
  const thickness = props.dripstone_thickness ?? 'tip';
  const half = DRIPSTONE_HALF[thickness] ?? 0.125;
  const down = (props.vertical_direction ?? 'down') === 'down';
  if (thickness !== 'tip') {
    return [[0.5 - half, 0, 0.5 - half, 0.5 + half, 1, 0.5 + half]];
  }
  // Three courses, narrowing toward the point, ending 3/16 short of it.
  const out: Box[] = [];
  const steps = [[0, 0.4375, half], [0.4375, 0.6875, half * 0.7], [0.6875, 0.8125, half * 0.4]] as const;
  for (const [lo, hi, w] of steps) {
    // Measured from the attached face: a hanging spike tapers downward, a standing
    // one upward, so `down` flips the run.
    const y0 = down ? 1 - hi : lo;
    const y1 = down ? 1 - lo : hi;
    out.push([0.5 - w, y0, 0.5 - w, 0.5 + w, y1, 0.5 + w]);
  }
  return out;
}

/** Bed mattress height (9/16), matching Minecraft's bed hitbox. */
const BED_H = 9 / 16;

/** Boxes for a connecting shape: a centre post plus an arm toward each connected
 *  side. `post` is the half-width of the post, `arm` the half-width of an arm. */
function connectedBoxes(props: Readonly<Record<string, string>>, post: number, arm: number, up = true): Box[] {
  const boxes: Box[] = [[0.5 - post, 0, 0.5 - post, 0.5 + post, up ? 1 : 0.9375, 0.5 + post]];
  const lo = 0.5 - arm, hi = 0.5 + arm, h = 0.9375;
  if (props.north === 'true') boxes.push([lo, 0.375, 0, hi, h, 0.5]);
  if (props.south === 'true') boxes.push([lo, 0.375, 0.5, hi, h, 1]);
  if (props.west === 'true') boxes.push([0, 0.375, lo, 0.5, h, hi]);
  if (props.east === 'true') boxes.push([0.5, 0.375, lo, 1, h, hi]);
  return boxes;
}

/**
 * The thin panel a door occupies: a full-height slab on its `facing` edge when
 * closed; opening swings it 90° CW to the adjacent edge, clearing the doorway
 * (M6 simplified single-property door — hinge/two-tall parity is M10.2).
 */
function doorBox(props: Readonly<Record<string, string>>): Box {
  const facing = props.facing ?? 'north';
  const open = props.open === 'true';
  // Closed edge per facing; opening rotates the panel one quarter-turn CW.
  const edge = open ? rotateFacingCW(facing) : facing;
  switch (edge) {
    case 'north': return [0, 0, 0, 1, 1, DOOR_T];
    case 'south': return [0, 0, 1 - DOOR_T, 1, 1, 1];
    case 'west': return [0, 0, 0, DOOR_T, 1, 1];
    case 'east': return [1 - DOOR_T, 0, 0, 1, 1, 1];
    default: return [0, 0, 0, 1, 1, DOOR_T];
  }
}

function rotateFacingCW(f: string): string {
  const ring = ['north', 'east', 'south', 'west'];
  const i = ring.indexOf(f);
  return i < 0 ? f : ring[(i + 1) % 4]!;
}

/**
 * Solid boxes for collision AND box-shape meshing. Cube = one full box; fluid,
 * cross, torch have none (swim-through / walk-through). Stairs: a bottom (or top)
 * slab plus a raised step on the side away from `facing`.
 */
export function blockBoxes(shape: number, props: Readonly<Record<string, string>>): Box[] {
  switch (shape) {
    case SHAPE.CUBE:
      return [FULL];
    case SHAPE.FLUID:
    case SHAPE.CROSS:
    case SHAPE.TORCH:
    case SHAPE.PORTAL:
      return []; // passable + not a mining raycast target
    case SHAPE.SLAB: {
      const type = props.type ?? 'bottom';
      if (type === 'double') return [FULL];
      return type === 'top' ? [[0, 0.5, 0, 1, 1, 1]] : [[0, 0, 0, 1, 0.5, 1]];
    }
    case SHAPE.LAYER: {
      const n = Math.max(1, Math.min(8, Number(props.layers ?? '1')));
      return [[0, 0, 0, 1, n / 8, 1]];
    }
    case SHAPE.DRIPSTONE:
      return dripstoneBoxes(props);
    case SHAPE.DOOR:
      return [doorBox(props)];
    case SHAPE.BED:
      return [[0, 0, 0, 1, BED_H, 1]];
    case SHAPE.FENCE:
      return connectedBoxes(props, 0.125, 0.0625);
    case SHAPE.WALL:
      return connectedBoxes(props, 0.1875, 0.125);
    case SHAPE.PANE:
      return connectedBoxes(props, 0.0625, 0.0625);
    case SHAPE.FENCE_GATE: {
      if (props.open === 'true') return [];
      const facing = props.facing ?? 'north';
      // Closed gate: a bar across the opening, perpendicular to `facing`.
      return facing === 'north' || facing === 'south'
        ? [[0, 0, 0.375, 1, 1, 0.625]]
        : [[0.375, 0, 0, 0.625, 1, 1]];
    }
    case SHAPE.TRAPDOOR: {
      if (props.open === 'true') {
        const facing = props.facing ?? 'north';
        switch (facing) {
          case 'south': return [[0, 0, 1 - DOOR_T, 1, 1, 1]];
          case 'west': return [[0, 0, 0, DOOR_T, 1, 1]];
          case 'east': return [[1 - DOOR_T, 0, 0, 1, 1, 1]];
          default: return [[0, 0, 0, 1, 1, DOOR_T]]; // north
        }
      }
      return props.half === 'top' ? [[0, 1 - DOOR_T, 0, 1, 1, 1]] : [[0, 0, 0, 1, DOOR_T, 1]];
    }
    case SHAPE.STAIRS: {
      const facing = props.facing ?? 'north';
      const top = props.half === 'top';
      const base: Box = top ? [0, 0.5, 0, 1, 1, 1] : [0, 0, 0, 1, 0.5, 1];
      const yl = top ? 0 : 0.5;
      const yh = top ? 0.5 : 1;
      // Raised step occupies the half away from the facing (low) side.
      let step: Box;
      switch (facing) {
        case 'north': step = [0, yl, 0.5, 1, yh, 1]; break; // low faces -z → step at +z
        case 'south': step = [0, yl, 0, 1, yh, 0.5]; break;
        case 'east': step = [0, yl, 0, 0.5, yh, 1]; break; // low faces +x → step at -x
        case 'west': step = [0.5, yl, 0, 1, yh, 1]; break;
        default: step = [0, yl, 0.5, 1, yh, 1];
      }
      return [base, step];
    }
    default:
      return [FULL];
  }
}
